// The anchored core — a fixed, versioned instrument.
//
// Before this, every question was generated fresh by the model from the Learning
// Science Framework §2.3 templates, so no two learners ever answered the same
// instrument. Reliability could not be computed even in principle, which made the
// promotion bar (intent decision 4) structurally unreachable: you cannot report
// Cronbach's alpha on a questionnaire that nobody took twice.
//
// Intent decision 2: 3–5 fixed items per dimension, relayed by Claude VERBATIM and
// scored by fixed rules. The model keeps the conversation — the opener, the
// transitions, the clarifying follow-up on an ambiguous answer. It no longer
// authors the items and it no longer produces the scores.
//
// ADVERSARIAL1 gives >= 3 items per subscale as the floor for internal
// consistency. anchor-scoring.test.ts asserts that floor; do not drop below it.
//
// VERSIONING: never edit an item's text or scoring in place. Add a new set with a
// new version, so a profile scored under 'anchor-v1' stays comparable with every
// other 'anchor-v1' profile. Beta wave 1 will revise this set.

import type { DiagnosticDimensionKey } from '../types/cogniprint';

export const ANCHOR_SET_VERSION = 'anchor-v1';

/** How an item's option contributions become a dimension score. */
export type CombineRule =
  /** Sum contributions per key, then normalise the set to sum to 100. */
  | 'sum_normalise'
  /** Average the contributions per key across the scored items. */
  | 'mean';

export interface AnchorOption {
  id: string;
  label: string;
  /**
   * What choosing this option contributes. Keys are the dimension's own subscale
   * names ('visual', 'topDown', …) or 'score' for a single-value dimension.
   */
  contributions: Record<string, number>;
}

export interface AnchorItem {
  id: string;
  dimension: DiagnosticDimensionKey;
  /** Printed to the learner by the app, verbatim. */
  stem: string;
  options: AnchorOption[];
  /**
   * Not part of the main sequence. Held back so a learner who disputes a result
   * can be asked one more question about that dimension and have it re-scored
   * from evidence — the only way a result changes after the fact.
   */
  reserve?: boolean;
}

export interface DimensionSpec {
  dimension: DiagnosticDimensionKey;
  tier: 'free' | 'paid';
  combine: CombineRule;
  /** Subscale keys, in the order the profile type expects them. */
  keys: string[];
  /** The value each key takes when the dimension could not be scored. */
  neutral: Record<string, number>;
}

export const DIMENSION_SPECS: Record<DiagnosticDimensionKey, DimensionSpec> = {
  contentFormat: {
    dimension: 'contentFormat', tier: 'free', combine: 'sum_normalise',
    keys: ['visual', 'textual', 'auditory', 'interactive', 'video'],
    neutral: { visual: 20, textual: 20, auditory: 20, interactive: 20, video: 20 },
  },
  learningApproach: {
    dimension: 'learningApproach', tier: 'free', combine: 'mean',
    keys: ['theoryApplication', 'reflectiveActive'],
    neutral: { theoryApplication: 50, reflectiveActive: 50 },
  },
  depthTolerance: {
    dimension: 'depthTolerance', tier: 'free', combine: 'mean',
    keys: ['score'], neutral: { score: 50 },
  },
  sequencing: {
    dimension: 'sequencing', tier: 'free', combine: 'sum_normalise',
    keys: ['topDown', 'bottomUp', 'modular'],
    neutral: { topDown: 34, bottomUp: 33, modular: 33 },
  },
  infoDensity: {
    dimension: 'infoDensity', tier: 'paid', combine: 'mean',
    keys: ['score'], neutral: { score: 50 },
  },
  motivation: {
    dimension: 'motivation', tier: 'paid', combine: 'sum_normalise',
    keys: ['mastery', 'performance', 'social', 'curiosity'],
    neutral: { mastery: 25, performance: 25, social: 25, curiosity: 25 },
  },
  viewpointRange: {
    dimension: 'viewpointRange', tier: 'paid', combine: 'mean',
    keys: ['score'], neutral: { score: 50 },
  },
};

// ─── Writing rules for every item ──────────────────────────────────────────
//
// JB's review of the first live run (2026-09-05): "The questions need to use
// language that can be understood by anyone that isn't technical across all
// domains. And perhaps provide an example." Also: "Questions need a little bit
// more context."
//
//   • Everyday situations only — cooking, furniture, a new town, a form, a phone.
//     A nurse, a plumber, a teenager and a lawyer must all recognise the scene.
//   • No jargon. Not "edge case", "framework", "first principles", "documentation",
//     "iterate". If a word would need explaining, it is the wrong word.
//   • Two sentences of set-up are fine. One concrete example beats an abstraction.
//   • Options describe something a person would actually do or say.
//   • Parallel in what they measure, never parallel in wording.
//
// Each dimension also carries ONE `reserve` item. It is never in the main sequence;
// it exists so a learner who disputes a result can be asked one more question about
// that dimension and have it re-scored — from evidence, not from an override.

// ─── Content Format ─────────────────────────────────────────────────────────
// Subscales: visual / textual / auditory / interactive / video.

const CONTENT_FORMAT_ITEMS: AnchorItem[] = [
  {
    id: 'cf-1', dimension: 'contentFormat',
    stem: "You have to get your head around something completely new by tomorrow — say, how a pension works, or how to use a new phone. You have an hour tonight. What do you reach for first?",
    options: [
      { id: 'a', label: 'A picture or diagram that shows how it all fits together', contributions: { visual: 100 } },
      { id: 'b', label: 'A clear written explanation I can read at my own speed', contributions: { textual: 100 } },
      { id: 'c', label: 'Someone explaining it out loud — a friend, a podcast, a radio piece', contributions: { auditory: 100 } },
      { id: 'd', label: 'Just start doing it and work it out as I go', contributions: { interactive: 100 } },
      { id: 'e', label: 'A video of someone showing me', contributions: { video: 100 } },
    ],
  },
  {
    id: 'cf-2', dimension: 'contentFormat',
    stem: "Flat-pack furniture, and the instructions are missing from the box. How are you getting this thing built?",
    options: [
      { id: 'a', label: 'Look up the picture that shows all the parts and how they join', contributions: { visual: 100 } },
      { id: 'b', label: 'Find the written instructions online and follow them step by step', contributions: { textual: 100 } },
      { id: 'c', label: 'Phone someone who has built one and let them talk me through it', contributions: { auditory: 100 } },
      { id: 'd', label: 'Lay everything out on the floor and figure it out by trying', contributions: { interactive: 100 } },
      { id: 'e', label: 'Find someone putting one together on YouTube', contributions: { video: 100 } },
    ],
  },
  {
    id: 'cf-3', dimension: 'contentFormat',
    stem: "Someone at work is leaving, and you're taking over a job they do every week — say, the monthly ordering, or closing up the shop. What do you ask them for before they go?",
    options: [
      { id: 'a', label: 'Draw me how it flows, start to finish', contributions: { visual: 100 } },
      { id: 'b', label: 'Write it down for me so I can read it later', contributions: { textual: 100 } },
      { id: 'c', label: 'Just talk me through it', contributions: { auditory: 100 } },
      { id: 'd', label: 'Let me do it once while you watch', contributions: { interactive: 100 } },
      { id: 'e', label: 'Film yourself doing it so I can watch it back', contributions: { video: 100 } },
    ],
  },
  {
    id: 'cf-r', dimension: 'contentFormat', reserve: true,
    stem: "Think of the last thing you learned that actually stuck — a recipe, a route, a new app, anything. How did it get into your head?",
    options: [
      { id: 'a', label: 'I saw it — a picture, a chart, a map', contributions: { visual: 100 } },
      { id: 'b', label: 'I read it', contributions: { textual: 100 } },
      { id: 'c', label: 'Someone told me, or I heard it', contributions: { auditory: 100 } },
      { id: 'd', label: 'I did it, badly, until I could do it well', contributions: { interactive: 100 } },
      { id: 'e', label: 'I watched someone do it', contributions: { video: 100 } },
    ],
  },
];

// ─── Learning Approach ──────────────────────────────────────────────────────
// Two axes, 0–100 each: theoryApplication (0 = practice-first, 100 = understand-first)
// and reflectiveActive (0 = doing, 100 = watching/thinking). Every option loads on
// both axes, so four items give each axis four loadings.

const LEARNING_APPROACH_ITEMS: AnchorItem[] = [
  {
    id: 'la-1', dimension: 'learningApproach',
    stem: 'You get a new gadget — a phone, a coffee machine, a fitness watch. What actually happens in the first ten minutes?',
    options: [
      { id: 'a', label: 'I read the instructions before I touch anything', contributions: { theoryApplication: 90, reflectiveActive: 80 } },
      { id: 'b', label: 'I skim the quick-start card, then start pressing buttons', contributions: { theoryApplication: 65, reflectiveActive: 35 } },
      { id: 'c', label: 'I watch someone else use it first', contributions: { theoryApplication: 40, reflectiveActive: 85 } },
      { id: 'd', label: 'I switch it on and start pressing things', contributions: { theoryApplication: 10, reflectiveActive: 10 } },
    ],
  },
  {
    id: 'la-2', dimension: 'learningApproach',
    stem: "You're learning something new — driving, a language, a sport — and you've hit the stage where you're obviously bad at it. What actually gets you past that?",
    options: [
      { id: 'a', label: 'Going back to understand why it is supposed to work', contributions: { theoryApplication: 90, reflectiveActive: 75 } },
      { id: 'b', label: 'Stopping for a bit and thinking about what keeps going wrong', contributions: { theoryApplication: 55, reflectiveActive: 90 } },
      { id: 'c', label: 'Watching someone who is good at it and copying them', contributions: { theoryApplication: 35, reflectiveActive: 60 } },
      { id: 'd', label: 'Just keeping at it until something clicks', contributions: { theoryApplication: 10, reflectiveActive: 15 } },
    ],
  },
  {
    id: 'la-3', dimension: 'learningApproach',
    stem: "A free Saturday afternoon, and there's something you've been wanting to learn. Which version of that afternoon sounds best?",
    options: [
      { id: 'a', label: 'Properly understanding how the whole thing works, start to finish', contributions: { theoryApplication: 95, reflectiveActive: 70 } },
      { id: 'b', label: 'Reading or watching a few different people explain it, and making up my own mind', contributions: { theoryApplication: 75, reflectiveActive: 90 } },
      { id: 'c', label: 'Having a go at making something, even if it comes out rough', contributions: { theoryApplication: 25, reflectiveActive: 20 } },
      { id: 'd', label: 'Talking it over with someone until it makes sense', contributions: { theoryApplication: 45, reflectiveActive: 55 } },
    ],
  },
  {
    id: 'la-4', dimension: 'learningApproach',
    stem: "Someone shows you how to do something — fold a fitted sheet, use a new till, tie a knot — and then says 'go on, you try'. What's your honest reaction?",
    options: [
      { id: 'a', label: "Not yet — show me once more so I really get it", contributions: { theoryApplication: 85, reflectiveActive: 85 } },
      { id: 'b', label: 'OK, but stay next to me for the first one', contributions: { theoryApplication: 60, reflectiveActive: 60 } },
      { id: 'c', label: "Yes — trying it is how I'll find out what I actually understood", contributions: { theoryApplication: 20, reflectiveActive: 25 } },
      { id: 'd', label: 'I already had a go while you were still talking', contributions: { theoryApplication: 5, reflectiveActive: 5 } },
    ],
  },
  {
    id: 'la-r', dimension: 'learningApproach', reserve: true,
    stem: "You're following a recipe you've never made before. Which is closest to how you'd actually cook it?",
    options: [
      { id: 'a', label: 'Read the whole recipe twice before I touch a pan', contributions: { theoryApplication: 90, reflectiveActive: 85 } },
      { id: 'b', label: 'Read the next step as I go', contributions: { theoryApplication: 50, reflectiveActive: 40 } },
      { id: 'c', label: 'Watch a video of it once, then cook from memory', contributions: { theoryApplication: 40, reflectiveActive: 80 } },
      { id: 'd', label: 'Glance at it, then taste and adjust as I go', contributions: { theoryApplication: 10, reflectiveActive: 10 } },
    ],
  },
];

// ─── Depth Tolerance ────────────────────────────────────────────────────────
// Single 0–100 scale. Low = just the essentials, high = every last detail.

const DEPTH_TOLERANCE_ITEMS: AnchorItem[] = [
  {
    id: 'dt-1', dimension: 'depthTolerance',
    stem: "You need to use something, not fix it — a new washing machine, say, or the online banking app. How much do you want to know about how it works underneath?",
    options: [
      { id: 'a', label: 'Just enough to use it', contributions: { score: 12 } },
      { id: 'b', label: 'The general idea of what it is doing', contributions: { score: 38 } },
      { id: 'c', label: 'Enough that I could explain it to someone else', contributions: { score: 65 } },
      { id: 'd', label: 'All of it — I want to know why it was made that way', contributions: { score: 92 } },
    ],
  },
  {
    id: 'dt-2', dimension: 'depthTolerance',
    stem: "Someone is explaining something to you and says: 'There's one unusual situation where this works differently, but it almost never comes up.' What do you want them to do?",
    options: [
      { id: 'a', label: "Skip it — just tell me what normally happens", contributions: { score: 10 } },
      { id: 'b', label: 'Give me one sentence on it, then move on', contributions: { score: 40 } },
      { id: 'c', label: 'Explain it properly — it might matter one day', contributions: { score: 70 } },
      { id: 'd', label: 'Explain it, and tell me why that unusual situation exists at all', contributions: { score: 95 } },
    ],
  },
  {
    id: 'dt-3', dimension: 'depthTolerance',
    stem: "You're in a meeting, or a doctor's appointment, and the person says 'I'll spare you the details'. What goes through your head?",
    options: [
      { id: 'a', label: 'Thank you — genuinely', contributions: { score: 15 } },
      { id: 'b', label: "Fine, as long as I know where to find the details if I want them", contributions: { score: 50 } },
      { id: 'c', label: "Please don't — the details are the interesting part", contributions: { score: 85 } },
    ],
  },
  {
    id: 'dt-r', dimension: 'depthTolerance', reserve: true,
    stem: "You're buying something you don't know much about — a mattress, a laptop, a car. How much research do you actually do?",
    options: [
      { id: 'a', label: 'Ask one person I trust and go with that', contributions: { score: 12 } },
      { id: 'b', label: 'Read a couple of reviews and pick', contributions: { score: 40 } },
      { id: 'c', label: 'Compare the main options properly before deciding', contributions: { score: 70 } },
      { id: 'd', label: 'By the end I could write the buying guide myself', contributions: { score: 95 } },
    ],
  },
];

// ─── Sequencing ─────────────────────────────────────────────────────────────
// Subscales: topDown (big picture first) / bottomUp (basics first) / modular
// (jump to what I need).

const SEQUENCING_ITEMS: AnchorItem[] = [
  {
    id: 'sq-1', dimension: 'sequencing',
    stem: "You've signed up for a course with twelve lessons — cooking, coding, first aid, whatever it is. How do you want to start?",
    options: [
      { id: 'a', label: 'Show me an overview of all twelve first, so I can see where it is going', contributions: { topDown: 100 } },
      { id: 'b', label: 'Start at lesson one and build up from there', contributions: { bottomUp: 100 } },
      { id: 'c', label: 'Let me jump to whichever lesson I need right now', contributions: { modular: 100 } },
    ],
  },
  {
    id: 'sq-2', dimension: 'sequencing',
    stem: "First morning in a city you've never been to. Three days, no plan. What do you do first?",
    options: [
      { id: 'a', label: 'Get up somewhere high and look at the whole place', contributions: { topDown: 100 } },
      { id: 'b', label: 'Start at the hotel door and explore outwards, street by street', contributions: { bottomUp: 100 } },
      { id: 'c', label: 'Pick the one thing I came to see and go straight there', contributions: { modular: 100 } },
    ],
  },
  {
    id: 'sq-3', dimension: 'sequencing',
    stem: 'Think back to something you tried to learn that annoyed you. Which of these was most likely the reason?',
    options: [
      { id: 'a', label: "They gave me loads of detail before I knew what any of it was for", contributions: { topDown: 100 } },
      { id: 'b', label: 'They threw me in the deep end before I had the basics', contributions: { bottomUp: 100 } },
      { id: 'c', label: 'They made me sit through stuff I already knew', contributions: { modular: 100 } },
    ],
  },
  {
    id: 'sq-r', dimension: 'sequencing', reserve: true,
    stem: "You open a long document — a contract, a manual, a report. What's the very first thing you do?",
    options: [
      { id: 'a', label: 'Read the contents page and the summary at the end', contributions: { topDown: 100 } },
      { id: 'b', label: 'Start at page one and read through', contributions: { bottomUp: 100 } },
      { id: 'c', label: 'Search for the one bit I actually need', contributions: { modular: 100 } },
    ],
  },
];

// ─── Information Density ────────────────────────────────────────────────────
// Single 0–100 scale. Low = small pieces with room to breathe, high = lots at once.
// v1 is self-report; the timed comprehension task from PE-003 is deferred to
// anchor-v2 because it needs response timing captured at the UI.

const INFO_DENSITY_ITEMS: AnchorItem[] = [
  {
    id: 'id-1', dimension: 'infoDensity',
    stem: "Two versions of the same guide. One is short sections with lots of white space and one idea at a time. The other is longer, denser pages that get through more at once. Same information in both. Which do you actually want?",
    options: [
      { id: 'a', label: 'The short, spaced-out one', contributions: { score: 15 } },
      { id: 'b', label: 'Somewhere in between', contributions: { score: 50 } },
      { id: 'c', label: "The dense one — I'd rather have it all in front of me", contributions: { score: 85 } },
    ],
  },
  {
    id: 'id-2', dimension: 'infoDensity',
    stem: "You're reading something that takes real concentration — a legal letter, a medical leaflet, instructions for a tax form. When do you need to stop and let it sink in?",
    options: [
      { id: 'a', label: 'After every new point', contributions: { score: 12 } },
      { id: 'b', label: 'After a few related points', contributions: { score: 45 } },
      { id: 'c', label: 'At the end of a whole section, usually', contributions: { score: 80 } },
      { id: 'd', label: "I stop when I get tired, not when the page tells me to", contributions: { score: 95 } },
    ],
  },
  {
    id: 'id-3', dimension: 'infoDensity',
    stem: "Everyone has complained about how something was written. Which of these two complaints is more likely to have come out of your mouth?",
    options: [
      { id: 'a', label: "\"That's a wall of text — I can't take it all in\"", contributions: { score: 18 } },
      { id: 'b', label: "Honestly, neither of those is really me", contributions: { score: 50 } },
      { id: 'c', label: "\"That's padded out — just get to the point\"", contributions: { score: 82 } },
    ],
  },
  {
    id: 'id-r', dimension: 'infoDensity', reserve: true,
    stem: "Someone sends you directions to their house. Which would you rather get?",
    options: [
      { id: 'a', label: 'Three or four short lines, one turn each', contributions: { score: 15 } },
      { id: 'b', label: 'A short paragraph', contributions: { score: 50 } },
      { id: 'c', label: 'Everything in one go — every landmark, every turn', contributions: { score: 85 } },
    ],
  },
];

// ─── Motivational Driver ────────────────────────────────────────────────────
// Subscales: mastery / performance / social / curiosity.

const MOTIVATION_ITEMS: AnchorItem[] = [
  {
    id: 'mo-1', dimension: 'motivation',
    stem: "You've just finished something genuinely hard — passed a test, fixed the thing, ran the distance. What's the best bit, honestly?",
    options: [
      { id: 'a', label: "Knowing I'm actually better at this now", contributions: { mastery: 100 } },
      { id: 'b', label: 'The result — it worked, and I can show people', contributions: { performance: 100 } },
      { id: 'c', label: 'Telling someone about it', contributions: { social: 100 } },
      { id: 'd', label: 'Finally knowing the thing I was wondering about', contributions: { curiosity: 100 } },
    ],
  },
  {
    id: 'mo-2', dimension: 'motivation',
    stem: "You're learning something and it's got boring. Nobody would notice if you quietly stopped. What most often keeps you going?",
    options: [
      { id: 'a', label: 'I said I would get good at this, and I meant it', contributions: { mastery: 100 } },
      { id: 'b', label: 'A score to beat, a streak to keep, a deadline coming', contributions: { performance: 100 } },
      { id: 'c', label: "Someone else is doing it with me", contributions: { social: 100 } },
      { id: 'd', label: 'I want to know what comes next', contributions: { curiosity: 100 } },
    ],
  },
  {
    id: 'mo-3', dimension: 'motivation',
    stem: "Six months from now, which of these would you be most pleased to have got out of learning something new?",
    options: [
      { id: 'a', label: 'A skill I can rely on for the rest of my life', contributions: { mastery: 100 } },
      { id: 'b', label: 'Something finished that I can point to', contributions: { performance: 100 } },
      { id: 'c', label: 'People I can talk about it with', contributions: { social: 100 } },
      { id: 'd', label: 'Ten more things I now want to find out about', contributions: { curiosity: 100 } },
    ],
  },
  {
    id: 'mo-r', dimension: 'motivation', reserve: true,
    stem: "A friend asks why you're bothering to learn the thing you're learning. What's the truthful answer?",
    options: [
      { id: 'a', label: 'Because I want to be properly good at it', contributions: { mastery: 100 } },
      { id: 'b', label: "Because there's something I need to pass, win or finish", contributions: { performance: 100 } },
      { id: 'c', label: 'Because the people doing it are people I want to be around', contributions: { social: 100 } },
      { id: 'd', label: "Because I can't stop wondering about it", contributions: { curiosity: 100 } },
    ],
  },
];

// ─── Viewpoint Range ────────────────────────────────────────────────────────
// Single 0–100 scale. Low = give me the one clear answer; high = show me the
// disagreement. NOT a measure of intelligence — both ends are legitimate, and the
// copy must never suggest otherwise.

const VIEWPOINT_RANGE_ITEMS: AnchorItem[] = [
  {
    id: 'vr-1', dimension: 'viewpointRange',
    stem: "You're learning about something where the experts genuinely disagree — how best to sleep-train a baby, say, or whether a particular diet works. What should we do about that?",
    options: [
      { id: 'a', label: 'Give me the answer most experts back, and move on', contributions: { score: 15 } },
      { id: 'b', label: 'Mention that there is a disagreement, briefly', contributions: { score: 50 } },
      { id: 'c', label: 'Show me the disagreement properly, both sides', contributions: { score: 88 } },
    ],
  },
  {
    id: 'vr-2', dimension: 'viewpointRange',
    stem: "You ask a doctor, a mechanic or a lawyer a straight question, and the answer is: 'Well, it depends.' What's your honest reaction?",
    options: [
      { id: 'a', label: "Frustrated — just tell me what to do", contributions: { score: 12 } },
      { id: 'b', label: "Fine, as long as they tell me what it depends on", contributions: { score: 55 } },
      { id: 'c', label: "Relieved — that's usually the honest answer", contributions: { score: 90 } },
    ],
  },
  {
    id: 'vr-3', dimension: 'viewpointRange',
    stem: "Two friends offer to help you understand something. One will give you the single clearest explanation they know. The other will show you three different ways people look at it. Which friend are you calling?",
    options: [
      { id: 'a', label: 'The one clear explanation, please', contributions: { score: 18 } },
      { id: 'b', label: 'The clear explanation, plus one good reason someone might disagree', contributions: { score: 52 } },
      { id: 'c', label: 'All three ways of looking at it', contributions: { score: 85 } },
    ],
  },
  {
    id: 'vr-r', dimension: 'viewpointRange', reserve: true,
    stem: "You're reading a news story about something complicated. Which kind of article do you actually finish?",
    options: [
      { id: 'a', label: 'The one that tells me plainly what happened and what it means', contributions: { score: 15 } },
      { id: 'b', label: 'The one that explains it, then gives the other side a paragraph', contributions: { score: 52 } },
      { id: 'c', label: 'The one that lays out every side and lets me decide', contributions: { score: 88 } },
    ],
  },
];

/** Grouped by dimension — the maintenance view. Presentation order is interleaved. */
const ITEMS_BY_DIMENSION: AnchorItem[][] = [
  CONTENT_FORMAT_ITEMS,
  LEARNING_APPROACH_ITEMS,
  DEPTH_TOLERANCE_ITEMS,
  SEQUENCING_ITEMS,
  INFO_DENSITY_ITEMS,
  MOTIVATION_ITEMS,
  VIEWPOINT_RANGE_ITEMS,
];

/**
 * Round-robin the dimension groups so consecutive items are never from the same
 * dimension.
 *
 * Three items measuring the same thing, asked back to back, read as being asked the
 * same question three times however differently they are worded — which is precisely
 * what "a chat, not a test" is not. Interleaving costs nothing psychometrically (the
 * items are scored per dimension regardless of order) and is most of what makes the
 * instrument feel like a conversation.
 */
function interleave(groups: AnchorItem[][]): AnchorItem[] {
  const main = groups.map((g) => g.filter((i) => !i.reserve));
  const out: AnchorItem[] = [];
  const longest = Math.max(0, ...main.map((g) => g.length));
  for (let round = 0; round < longest; round++) {
    for (const group of main) {
      if (group[round]) out.push(group[round]);
    }
  }
  return out;
}

/** Every item in the set, reserves included, grouped by dimension. */
export const ANCHOR_SET_V1_ALL: AnchorItem[] = ITEMS_BY_DIMENSION.flat();

/** The main sequence — reserves excluded — in presentation order. */
export const ANCHOR_SET_V1: AnchorItem[] = interleave(ITEMS_BY_DIMENSION);

/** The items a given diagnostic mode presents, in presentation order. */
export function anchorItemsForMode(mode: 'quick' | 'full'): AnchorItem[] {
  return mode === 'full'
    ? ANCHOR_SET_V1
    : interleave(ITEMS_BY_DIMENSION.filter((g) => DIMENSION_SPECS[g[0].dimension].tier === 'free'));
}

/** The held-back item for a dimension, for the dispute flow. */
export function reserveItemFor(dimension: DiagnosticDimensionKey): AnchorItem | undefined {
  return ANCHOR_SET_V1_ALL.find((i) => i.dimension === dimension && i.reserve);
}

export function anchorItemById(itemId: string): AnchorItem | undefined {
  return ANCHOR_SET_V1_ALL.find((i) => i.id === itemId);
}
